import * as React from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { LoadingCircle } from '../common';
import styles from './HB1416Tracker.styles';
import HB1416TrackingStudent from './HB1416TrackingStudent';

const SUBJECT_LABELS = { math: 'Math', reading: 'Reading' };

// Backend completedHours already folds session minutes + ixlMinutes/60 (single source
// of truth), so read it directly. ixlMinutes is shown as an informational breakdown only.
const completedForSubject = (tracker, subject) => (tracker.completedHours && tracker.completedHours[subject]) || 0;

const percentForSubject = (tracker, subject) => {
  const required = (tracker.requiredHours && tracker.requiredHours[subject]) || 0;
  if (!required) return null;
  const completed = completedForSubject(tracker, subject);
  return Math.min(100, Math.round((completed / required) * 100));
};

const subjectsForTracker = (tracker) => {
  const required = (tracker && tracker.requiredHours) || {};
  return Object.keys(required).filter((subject) => required[subject]);
};

// Hours-completion margin: the lowest per-subject completion percent the backend
// emits (0-100, lower = more behind). Sorting ascending surfaces the most-behind first.
const completionMargin = (entry) => (entry && typeof entry.margin === 'number' ? entry.margin : 100);

class HB1416Tracker extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.object,
    profile: PropTypes.object,
    snapshotYear: PropTypes.string,
    init: PropTypes.func.isRequired,
    getHb1416Tracker: PropTypes.func.isRequired,
    updateTracker: PropTypes.func.isRequired,
    generateAEP: PropTypes.func.isRequired,
    selectStudent: PropTypes.func.isRequired,
    generateAllAEPs: PropTypes.func.isRequired,
    matchTiaDesignations: PropTypes.func.isRequired,
    bulkAep: PropTypes.object,
    tiaCheck: PropTypes.object,
    worklist: PropTypes.object,
    students: PropTypes.object,
    schoolList: PropTypes.object,
    avatars: PropTypes.object,
    loading: PropTypes.bool,
    loadingRoster: PropTypes.bool,
    loadingMsg: PropTypes.string,
    selectedStudentId: PropTypes.string,
    aepGenerating: PropTypes.object,
  };

  static defaultProps = {
    worklist: {},
    students: {},
    schoolList: {},
    aepGenerating: {},
  };

  state = {
    isLoadedBefore: false,
    campusFilter: 'all',
    // Bulk-AEP dialog: confirm phase until Generate is clicked, then the
    // bulkAep prop drives running/done/error.
    aepDialogOpen: false,
    aepDialogStarted: false,
    aepDialogLanguage: 'en',
    // TIA dialog opens straight into running (the check has no options).
    tiaDialogOpen: false,
  };

  componentDidMount() {
    this.shouldInit();
  }

  componentDidUpdate() {
    this.shouldInit();
  }

  shouldInit = () => {
    const { profile, init, getHb1416Tracker, snapshotYear } = this.props;
    const { isLoadedBefore } = this.state;
    if (profile && !isLoadedBefore) {
      this.setState({ isLoadedBefore: true });
      init(profile);
      getHb1416Tracker(snapshotYear);
    }
  };

  handleCampusChange = (event) => {
    this.setState({ campusFilter: event.target.value });
  };

  handleSelectStudent = (firebaseId) => {
    const { selectStudent, selectedStudentId } = this.props;
    selectStudent(selectedStudentId === firebaseId ? null : firebaseId);
  };

  handleOpenAepDialog = () => {
    this.setState({ aepDialogOpen: true, aepDialogStarted: false, aepDialogLanguage: 'en' });
  };

  handleCloseAepDialog = () => {
    this.setState({ aepDialogOpen: false, aepDialogStarted: false });
  };

  handleAepDialogLanguage = (event) => {
    this.setState({ aepDialogLanguage: event.target.value });
  };

  handleConfirmGenerateAll = () => {
    const { generateAllAEPs, snapshotYear } = this.props;
    const { aepDialogLanguage } = this.state;
    this.setState({ aepDialogStarted: true });
    generateAllAEPs(snapshotYear, aepDialogLanguage);
  };

  handleOpenTiaDialog = () => {
    const { matchTiaDesignations, snapshotYear } = this.props;
    this.setState({ tiaDialogOpen: true });
    matchTiaDesignations(snapshotYear);
  };

  handleCloseTiaDialog = () => {
    this.setState({ tiaDialogOpen: false });
  };

  // Students the bulk run will render for: AEP required and not excluded.
  aepEligibleCount() {
    const { worklist } = this.props;
    return Object.values(worklist).filter(
      (entry) => entry && !entry.excluded && entry.tracker && entry.tracker.aep && entry.tracker.aep.required
    ).length;
  }

  getSchoolName(campusNumber) {
    const { schoolList } = this.props;
    const school = schoolList && schoolList[campusNumber];
    return (school && school.name) || campusNumber || '';
  }

  campusOptions() {
    const { worklist } = this.props;
    const seen = {};
    Object.values(worklist).forEach((entry) => {
      const campusNumber = entry && entry.student && entry.student.campusNumber;
      if (campusNumber) seen[campusNumber] = this.getSchoolName(campusNumber);
    });
    return Object.entries(seen).map(([value, label]) => ({ value, label }));
  }

  sortedEntries() {
    const { worklist } = this.props;
    const { campusFilter } = this.state;
    return Object.entries(worklist)
      .filter(([, entry]) => {
        if (!entry) return false;
        if (campusFilter === 'all') return true;
        const student = entry.student || {};
        return String(student.campusNumber) === String(campusFilter);
      })
      .sort(([, a], [, b]) => completionMargin(a) - completionMargin(b));
  }

  renderPercentCell(tracker, subject) {
    const { classes } = this.props;
    const percent = percentForSubject(tracker, subject);
    if (percent === null) return <span>—</span>;
    const completed = completedForSubject(tracker, subject);
    const required = (tracker.requiredHours && tracker.requiredHours[subject]) || 0;
    return (
      <div>
        <div className={classes.hoursValue} style={{ fontSize: 12, marginBottom: 2 }}>
          {completed.toFixed(1)}/{required}h ({percent}%)
        </div>
        <div
          className={classes.progressBarOuter}
          style={{ width: 90, height: 8, borderRadius: 4, background: '#e0e0e0', overflow: 'hidden' }}
        >
          <div style={{ width: `${percent}%`, height: '100%', background: '#68d190', borderRadius: 4 }} />
        </div>
      </div>
    );
  }

  renderRows() {
    const {
      classes,
      students,
      snapshotYear,
      selectedStudentId,
      aepGenerating,
      updateTracker,
      generateAEP,
    } = this.props;
    const entries = this.sortedEntries();
    const rows = [];
    entries.forEach(([firebaseId, entry]) => {
      const student = (entry && entry.student) || students[firebaseId] || {};
      const tracker = (entry && entry.tracker) || {};
      const fullName = `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Student';
      const subjects = subjectsForTracker(tracker);
      const aepRequired = Boolean(tracker.aep && tracker.aep.required);
      const excluded = entry && entry.excluded;
      const isExpanded = selectedStudentId === firebaseId;
      rows.push(
        <TableRow key={firebaseId} hover>
          <TableCell>
            <span
              className={excluded ? `${classes.studentNameCell} ${classes.excludedName}` : classes.studentNameCell}
              role="button"
              tabIndex={0}
              onClick={() => this.handleSelectStudent(firebaseId)}
              onKeyPress={() => this.handleSelectStudent(firebaseId)}
            >
              {fullName}
            </span>
          </TableCell>
          <TableCell>{this.getSchoolName(student.campusNumber)}</TableCell>
          <TableCell>{subjects.map((subject) => SUBJECT_LABELS[subject] || subject).join(', ') || '—'}</TableCell>
          {excluded ? (
            <TableCell colSpan={2}>
              <Chip
                label={excluded.reason === 'tiaTeacher' ? 'Excluded — TIA teacher' : 'Excluded'}
                variant="outlined"
              />
            </TableCell>
          ) : (
            <React.Fragment>
              <TableCell>{this.renderPercentCell(tracker, 'math')}</TableCell>
              <TableCell>{this.renderPercentCell(tracker, 'reading')}</TableCell>
            </React.Fragment>
          )}
          <TableCell>
            {aepRequired && !excluded ? (
              <Chip className={classes.aepBadge} label="AEP required" color="secondary" />
            ) : (
              <span>—</span>
            )}
          </TableCell>
        </TableRow>
      );
      if (isExpanded) {
        rows.push(
          <TableRow key={`${firebaseId}-detail`}>
            <TableCell colSpan={6}>
              <HB1416TrackingStudent
                firebaseId={firebaseId}
                snapshotYear={snapshotYear}
                student={student}
                tracker={tracker}
                school={this.getSchoolName(student.campusNumber)}
                aepGenerating={aepGenerating}
                onLogSession={updateTracker}
                onUpdateFields={updateTracker}
                onGenerateAEP={generateAEP}
              />
            </TableCell>
          </TableRow>
        );
      }
    });
    return rows;
  }

  renderAepDialog() {
    const { bulkAep } = this.props;
    const { aepDialogOpen, aepDialogStarted, aepDialogLanguage } = this.state;
    const count = this.aepEligibleCount();
    const running = aepDialogStarted && bulkAep && bulkAep.status === 'running';
    const summary = (aepDialogStarted && bulkAep && bulkAep.status === 'done' && bulkAep.summary) || null;
    const error = aepDialogStarted && bulkAep && bulkAep.status === 'error' ? bulkAep.error : null;
    return (
      <Dialog open={aepDialogOpen} onClose={running ? undefined : this.handleCloseAepDialog}>
        <DialogTitle>Generate All AEPs</DialogTitle>
        <DialogContent>
          {!aepDialogStarted && (
            <div>
              <div style={{ marginBottom: 12 }}>
                This will generate an Accelerated Education Plan PDF for each of the {count} students currently
                requiring an AEP (excluded students are skipped). Plans use the standard narrative, or a student&#39;s
                saved custom narrative when one exists.
              </div>
              <RadioGroup row name="bulk-aep-lang" value={aepDialogLanguage} onChange={this.handleAepDialogLanguage}>
                <FormControlLabel value="en" control={<Radio color="primary" />} label="English" />
                <FormControlLabel value="es" control={<Radio color="primary" />} label="Spanish" />
              </RadioGroup>
            </div>
          )}
          {running && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <CircularProgress size={20} /> Generating AEP PDFs ... this can take a few minutes.
            </div>
          )}
          {summary && (
            <div>
              <div>Students with an AEP requirement: {summary.total}</div>
              <div>PDFs generated: {summary.generated}</div>
              <div>Skipped (excluded): {summary.skippedExcluded}</div>
              <div>Failed: {summary.failed}</div>
            </div>
          )}
          {error && <div style={{ color: '#d32f2f' }}>{error}</div>}
        </DialogContent>
        <DialogActions>
          {!aepDialogStarted ? (
            <React.Fragment>
              <Button onClick={this.handleCloseAepDialog}>Cancel</Button>
              <Button variant="contained" color="primary" disabled={!count} onClick={this.handleConfirmGenerateAll}>
                Generate {count} AEPs
              </Button>
            </React.Fragment>
          ) : (
            <Button onClick={this.handleCloseAepDialog} disabled={running}>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  renderTiaDialog() {
    const { tiaCheck } = this.props;
    const { tiaDialogOpen } = this.state;
    const running = tiaCheck && tiaCheck.status === 'running';
    const summary = (tiaCheck && tiaCheck.status === 'done' && tiaCheck.summary) || null;
    const error = tiaCheck && tiaCheck.status === 'error' ? tiaCheck.error : null;
    return (
      <Dialog open={tiaDialogOpen} onClose={running ? undefined : this.handleCloseTiaDialog}>
        <DialogTitle>TIA Designation Check</DialogTitle>
        <DialogContent>
          {running && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <CircularProgress size={20} /> Checking the TEA designation registry ...
            </div>
          )}
          {summary && (
            <div>
              <div style={{ marginBottom: 8 }}>
                Teachers checked: {summary.teachersChecked} · Students excluded: {summary.studentsExcluded}
              </div>
              {summary.confirmed && summary.confirmed.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 600 }}>Confirmed designations</div>
                  {summary.confirmed.map((row) => (
                    <div key={row.teacher}>
                      {row.teacher} — {row.designation} ({row.students} student{row.students === 1 ? '' : 's'} excluded)
                    </div>
                  ))}
                </div>
              )}
              {summary.ambiguous && summary.ambiguous.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 600 }}>Ambiguous (verify with HR)</div>
                  <div>
                    More than one registry entry matched these names, so no exclusions were applied for them:{' '}
                    {summary.ambiguous.join(', ')}
                  </div>
                </div>
              )}
              <div>Teachers with no active designation found: {summary.none}</div>
            </div>
          )}
          {error && <div style={{ color: '#d32f2f' }}>{error}</div>}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleCloseTiaDialog} disabled={!!running}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    const { classes, profile, loading, loadingRoster, loadingMsg, worklist, avatars } = this.props;
    const { campusFilter } = this.state;

    if (!profile || loadingRoster || loading || !avatars) {
      return (
        <div className={classes.content}>
          <LoadingCircle classes={classes} msg={loadingMsg} />
        </div>
      );
    }

    const isEmpty = !worklist || Object.keys(worklist).length === 0;
    const campusOptions = this.campusOptions();

    return (
      <div className={classes.root}>
        <div className={classes.headerRow}>
          <div className={classes.title}>HB 1416 Tracker</div>
          <div className={classes.headerActions}>
            <Button variant="outlined" color="primary" disabled={isEmpty} onClick={this.handleOpenTiaDialog}>
              Check TIA Designations
            </Button>
            <Button variant="contained" color="primary" disabled={isEmpty} onClick={this.handleOpenAepDialog}>
              Generate All AEPs
            </Button>
          </div>
        </div>
        <div className={classes.filterRow}>
          <FormControl className={classes.campusSelect}>
            <InputLabel htmlFor="hb1416-campus-filter">Campus</InputLabel>
            <Select value={campusFilter} onChange={this.handleCampusChange} inputProps={{ id: 'hb1416-campus-filter' }}>
              <MenuItem value="all">All campuses</MenuItem>
              {campusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {isEmpty ? (
          <div className={classes.emptyState}>
            No students are currently tagged for HB 1416. Tags are applied when assessment results are processed.
          </div>
        ) : (
          <div className={classes.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Campus</TableCell>
                  <TableCell>Subject(s)</TableCell>
                  <TableCell>Math hours</TableCell>
                  <TableCell>Reading hours</TableCell>
                  <TableCell>AEP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{this.renderRows()}</TableBody>
            </Table>
          </div>
        )}
        {this.renderAepDialog()}
        {this.renderTiaDialog()}
      </div>
    );
  }
}

export default withStyles(styles)(HB1416Tracker);
